import { useCurrentUserType } from "@/components/contexts/CurrentUserTypeContext";
import { useComebackUrl } from "@/components/hooks/use-comeback-url";
import type {
  ReactSerializable,
  ServerActionResult,
} from "@/server/actions/server-action-result";
import { appUrl } from "@/utils/app-url";
import { logger } from "@/utils/logger";
import { pagesPath } from "@/utils/pages-path";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import useSWR, { type Key, type SWRConfiguration } from "swr";
import { useShowErrorToast } from "./use-show-error-toast";

const COMMON_ERROR_MESSAGES = {
  UNAUTHORIZED: "ログインが必要です",
  FORBIDDEN: "権限がありません",
  UNKNOWN_ERROR: "不明なエラーが発生しました",
} as const;

class ServerActionError<Error extends string> extends Error {
  public error: Error;
  constructor(error: Error) {
    super(error);
    this.name = "ServerActionError";
    this.error = error;
  }
}

const NEXT_HTTP_ERROR_FALLBACK_PREFIX = "NEXT_HTTP_ERROR_FALLBACK";
/**
 * next/navigationのnotFound, forbidden, unauthorizedで投げられるエラーからステータスコードを抽出する
 */
export const extractNextFallbackError = (
  error: unknown,
): "notFound" | "forbidden" | "unauthorized" | null => {
  if (error instanceof Error) {
    if (error.message.startsWith(NEXT_HTTP_ERROR_FALLBACK_PREFIX)) {
      const statusPart = error.message.split(";").at(1)?.trim();
      switch (statusPart) {
        case "404":
          return "notFound";
        case "403":
          return "forbidden";
        case "401":
          return "unauthorized";
        default:
          return null;
      }
    }
  }
  return null;
};

const useHandleUnauthorized = () => {
  const router = useRouter();

  const { type: userType } = useCurrentUserType();
  const { setComebackUrl } = useComebackUrl();
  const pathname = usePathname();
  const loginUrl = useMemo(
    () =>
      userType === "employer"
        ? pagesPath.employers.login.$url().path
        : userType === "member"
          ? pagesPath.members.login.$url().path
          : pagesPath.admin.login.$url().path,
    [userType],
  );

  return () => {
    setComebackUrl(appUrl(pathname));
    router.push(loginUrl);
  };
};

const useHandleError = <ServerActionError>({
  onError,
  onNetworkError,
}: {
  onError?: (
    error: ServerActionError,
    showErrorToast: ReturnType<typeof useShowErrorToast>["showErrorToast"],
  ) => void | { unknownError: true };
  onNetworkError?: (error: unknown) => void;
}) => {
  const { showErrorToast } = useShowErrorToast();

  const showUnknownErrorToast = useCallback(() => {
    showErrorToast({
      description: COMMON_ERROR_MESSAGES.UNKNOWN_ERROR,
    });
  }, [showErrorToast]);
  const showForbiddenErrorToast = useCallback(() => {
    showErrorToast({
      description: COMMON_ERROR_MESSAGES.FORBIDDEN,
    });
  }, [showErrorToast]);
  const handleUnauthorized = useHandleUnauthorized();

  return useCallback(
    (error: unknown) => {
      // ServerActionのエラー
      if (error instanceof ServerActionError) {
        // ServerActionのエラーでCommonServerActionError相当ではないものはonErrorでハンドリング
        if (onError) {
          const t = onError(error.error, showErrorToast);
          // onErrorで不明なエラーと判定された場合は不明なエラーとして処理
          if (t && t.unknownError) {
            showUnknownErrorToast();
          }
        } else {
          showUnknownErrorToast();
        }
      } else {
        // ネットワークエラーなど不明なエラー
        onNetworkError?.(error);

        const nextFallback = extractNextFallbackError(error);
        if (nextFallback === "unauthorized") {
          handleUnauthorized();
        } else if (nextFallback === "forbidden") {
          showForbiddenErrorToast();
        } else {
          showUnknownErrorToast();
        }
      }
    },
    [
      handleUnauthorized,
      onError,
      onNetworkError,
      showErrorToast,
      showForbiddenErrorToast,
      showUnknownErrorToast,
    ],
  );
};

/**
 * useSWRをラップした **GET系の** Server Actionsを呼び出すためのカスタムフック
 *
 * 共通のエラーに対するエラーハンドリングなどが含まれる
 *
 * Server Actionsは並行実行されないためデータ取得のためだけにServer Actionsを利用するのはパフォーマンス的に望ましくないが許容している
 * @see docs/nextjs-guideline.md
 */
export const useServerAction = <
  ServerActionError extends string | void,
  Value extends ReactSerializable,
>(
  key: Key,
  serverAction: () => Promise<ServerActionResult<ServerActionError, Value>>,
  options?: {
    // ServerActionResultのエラーのカスタムハンドリング
    // 無いかunknownError: trueが返された場合は不明なエラーにフォールバックされる
    onError?: (
      error: ServerActionError,
      showErrorToast: ReturnType<typeof useShowErrorToast>["showErrorToast"],
    ) => void | { unknownError: true };
    // ServerActionResultのエラーではない不明なエラー（ネットワークエラーなど）のカスタムハンドリング
    // onErrorの方は共通のエラーハンドリングをオーバーライドするが、こちらはどうハンドリングしているかに限らず共通のエラーハンドリングが呼ばれる
    // つまりこの中でtoastなどを出すと二重になる
    onNetworkError?: (error: unknown) => void;
    swrOptions?: Omit<SWRConfiguration<Value>, "onError">;
  },
) => {
  const handleError = useHandleError<ServerActionError>({
    onError: options?.onError,
    onNetworkError: options?.onNetworkError,
  });

  return useSWR<Value, any, Key>(
    key,
    async (): Promise<Value> => {
      const res = await serverAction();
      if (!res.success) {
        if (typeof res.error === "string") {
          throw new ServerActionError(res.error);
        } else {
          // Errorがvoidのときはsuccessがfalseにならないはずだが型的に排除出来ない
          throw new Error("Unknown server action error");
        }
      }

      // @ts-expect-error
      return res.value as Value;
    },
    {
      ...(options?.swrOptions as any),
      onError: (error: unknown) => {
        handleError(error);
      },
    },
  );
};

/**
 * POST系（一般的なServer Actionsの使い方）のServer Actionsを呼び出すためのカスタムフック
 *
 * 共通のエラーに対するエラーハンドリングなどが含まれる
 */
export const useServerActionCall = <
  ServerActionError extends string | void,
  Value extends ReactSerializable | void,
  Params extends ReactSerializable[],
>(
  action: (
    ...params: Params
  ) => Promise<ServerActionResult<ServerActionError, Value>>,
  options?: {
    onSuccess?: (value: Value, params: Params) => void;
    finally?: () => void;
    // ServerActionResultのエラーのカスタムハンドリング
    // 無いかunknownError: trueが返された場合は不明なエラーにフォールバックされる
    onError?: (
      error: ServerActionError,
      showErrorToast: ReturnType<typeof useShowErrorToast>["showErrorToast"],
    ) => void | { unknownError: true };
    // ServerActionResultのエラーではない不明なエラー（ネットワークエラーなど）のカスタムハンドリング
    // onErrorの方は共通のエラーハンドリングをオーバーライドするが、こちらはどうハンドリングしているかに限らず共通のエラーハンドリングが呼ばれる
    // つまりこの中でtoastなどを出すと二重になる
    onNetworkError?: (error: unknown) => void;
  },
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleError = useHandleError<ServerActionError>({
    onError: options?.onError,
    onNetworkError: options?.onNetworkError,
  });

  const call = useCallback(
    async (...params: Params) => {
      try {
        setIsLoading(true);
        const res = await action(...params);
        if (!res.success) {
          if (typeof res.error === "string") {
            throw new ServerActionError(res.error);
          } else {
            // Errorがvoidのときはsuccessがfalseにならないはずだが型的に排除出来ない
            throw new Error("Unknown server action error");
          }
        }

        // @ts-expect-error
        const value = res.value as Value;
        options?.onSuccess?.(value, params);
        setIsSubmitted(true);
        return value;
      } catch (error: unknown) {
        // ネットワークエラーなど
        logger.error({ error }, "useServerActionCall: Unexpected error");

        handleError(error);
      } finally {
        setIsLoading(false);
        options?.finally?.();
      }

      return null;
    },
    [action, handleError, options],
  );

  return {
    isLoading,
    isSubmitted,
    call,
  };
};
